/**
 * Stimulus Controller for FAQ Accordion
 * Enhanced with smooth CSS-based animations for Connected Flow theme
 */

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "content", "icon"]

  connect() {
    // Initialize all items as collapsed (using CSS class-based animation)
    this.contentTargets.forEach(content => {
      content.classList.remove("expanded")
    })
    this.itemTargets.forEach(item => {
      item.classList.remove("expanded")
    })

    // Handle URL hash on page load
    this.handleUrlHash()

    // Listen for hash changes (browser back/forward, link clicks)
    this.boundHandleHashChange = this.handleUrlHash.bind(this)
    window.addEventListener('hashchange', this.boundHandleHashChange)
  }

  disconnect() {
    // Clean up event listener
    window.removeEventListener('hashchange', this.boundHandleHashChange)
  }

  toggle(event) {
    // Find the closest item container
    const item = event.currentTarget.closest("[data-accordion-target='item']")
    if (!item) return

    const content = item.querySelector("[data-accordion-target='content']")
    const icon = item.querySelector("[data-accordion-target='icon']")

    if (!content) return

    // Toggle visibility using CSS classes
    const isExpanded = content.classList.contains("expanded")

    if (!isExpanded) {
      // Expand
      content.classList.add("expanded")
      item.classList.add("expanded")
      if (icon) {
        icon.classList.add("rotate-180")
      }

      // Update URL hash when expanding (enables direct URL sharing)
      const itemId = item.id
      if (itemId) {
        history.pushState(null, null, `#${itemId}`)
      }
    } else {
      // Collapse
      content.classList.remove("expanded")
      item.classList.remove("expanded")
      if (icon) {
        icon.classList.remove("rotate-180")
      }

      // Remove URL hash when collapsing
      const currentHash = window.location.hash.slice(1)
      if (currentHash === item.id) {
        history.pushState(null, null, window.location.pathname)
      }
    }
  }

  // Optional: Expand all items
  expandAll() {
    this.contentTargets.forEach(content => {
      content.classList.add("expanded")
    })
    this.itemTargets.forEach(item => {
      item.classList.add("expanded")
    })
    this.iconTargets.forEach(icon => {
      icon.classList.add("rotate-180")
    })
  }

  // Optional: Collapse all items
  collapseAll() {
    this.contentTargets.forEach(content => {
      content.classList.remove("expanded")
    })
    this.itemTargets.forEach(item => {
      item.classList.remove("expanded")
    })
    this.iconTargets.forEach(icon => {
      icon.classList.remove("rotate-180")
    })
  }

  /**
   * Handle URL hash for anchor navigation
   * Automatically expands and scrolls to the target question
   */
  handleUrlHash() {
    // Get hash from URL (remove the # prefix)
    const hash = window.location.hash.slice(1)
    if (!hash) return

    // Find the target FAQ item by ID
    const targetItem = document.getElementById(hash)
    if (!targetItem) return

    // Collapse all questions first (per user requirement)
    this.collapseAll()

    // Find the target item's content and icon
    const content = targetItem.querySelector("[data-accordion-target='content']")
    const icon = targetItem.querySelector("[data-accordion-target='icon']")

    if (content) {
      // Expand the target question
      content.classList.add("expanded")
      targetItem.classList.add("expanded")
      if (icon) {
        icon.classList.add("rotate-180")
      }
    }

    // Scroll to the target, accounting for fixed navbar height (80px)
    // Use setTimeout to ensure DOM has finished rendering
    setTimeout(() => {
      const navbarHeight = 80
      const targetPosition = targetItem.getBoundingClientRect().top + window.pageYOffset - navbarHeight

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      })
    }, 100)
  }
}
